import { useContext, useEffect, useMemo, useState } from 'react'
import {
  ApprovalStatus,
  kuiperClassNames,
  navigateToContent,
} from '@opallabs/kuiper'
import { DotLoader, Tooltip, Icon } from '@opallabs/lapidary'
import { AllowedEvents, AnalyticsConsumer, TrackEvent } from '@opallabs/quanta'
import { ChannelIcon } from '@opallabs/satellite'
import CardContext from '../../../../../../cardContext'
import { PartialPost } from '../../../../../../momentTypes'
import { useWorkspaceServiceIconMap } from '../../../../../../queries/useSystemServices'
import { useIsContentOutsideMomentRange } from '../../../../../../useIsContentOutsideMomentRange'
import { WeekViewGlobalContext } from '../../../../../../weekViewGlobalContext'
import { setContentDragData } from '../../../../../dragData'
import RenamePopover from '../../../../../RenamePopover'
import { updateContentFromDrop } from '../updateContentFromDrop'
import defaultContentItemPreview from './dragPreview'
import './styles.scss'

const hasCustomDragPreview = () => window.safari // reasonable test for desktop Safari

type DefaultContentListItemProps = {
  className: string
  closeRenamePopover: () => void
  content: PartialPost
  contextMenuCallbacks: Record<string, any>
  index: number
  isLimitedUser: boolean
  isMomentDragActive: boolean
  isRenamePopoverOpen: boolean
  moment: Record<string, any>
  onContextMenu: (e: React.MouseEvent) => void
  story: Record<string, any>
}

export const DefaultContentListItem: React.VFC<DefaultContentListItemProps> = ({
  className,
  closeRenamePopover,
  content,
  contextMenuCallbacks,
  index,
  isLimitedUser,
  isMomentDragActive,
  isRenamePopoverOpen,
  moment,
  onContextMenu,
  story,
}) => {
  const [contentName, setContentName] = useState(content.name ?? '')
  const [dragCount, setDragCount] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [hasPlaceholder, setHasPlaceholder] = useState(false)

  const [customDragPreview] = useState<HTMLImageElement>(() => {
    const img = new Image()
    img.src = defaultContentItemPreview
    return img
  })

  const { currentBrandId } = useContext(WeekViewGlobalContext)

  const {
    navigateTo,
    setDragOrigin,
    onMoveContent,
    onReorderContent,
    setIsDraggingContent,
  } = useContext(CardContext) ?? {}

  const { data: serviceIconMap } = useWorkspaceServiceIconMap(currentBrandId)

  const serviceIcon = useMemo(
    () => serviceIconMap?.[content.service.id],
    [content.service.id, serviceIconMap]
  )

  const { isAfter: isScheduledAfterMoment } = useIsContentOutsideMomentRange(
    content,
    moment
  )

  useEffect(() => {
    setContentName(content.name)
  }, [content.name])

  const resetDragStates = () => {
    setDragCount(0)
    setIsDragging(false)
    setHasPlaceholder(false)
  }

  const isDraggable = () => {
    const draggableConditions = [!content.isSaving, !isLimitedUser]
    return draggableConditions.every((c) => c)
  }

  const onClickContent = ({
    navigateTo,
    track,
  }: {
    navigateTo?: (arg: any) => void
    track: TrackEvent
  }) => {
    if (isRenamePopoverOpen) {
      return
    }
    track(AllowedEvents.click, { id: content.id, type: 'content' })
    navigateToContent({
      contentId: content.id,
      momentId: moment.id,
      navigateTo,
      primaryBrand: moment.primaryBrand,
      storyId: story && story.id,
    })
  }

  const onDragContentListItem = ({
    e,
    track,
  }: {
    e: React.DragEvent
    track: TrackEvent
  }) => {
    e.stopPropagation()
    setIsDraggingContent?.(true)

    const dragData = {
      contentId: content.id,
      momentId: moment.id,
      contentPosition: index,
    }

    setContentDragData(e, dragData)

    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'move'
    }

    if (hasCustomDragPreview() && e.dataTransfer) {
      e.dataTransfer.setDragImage(customDragPreview, 40, 15)
    }

    setDragOrigin?.(dragData)
    track(AllowedEvents.drag, { id: content.id, type: 'content' })
    setTimeout(() => setIsDragging(true), 0)
  }

  const onDragEnd = () => {
    setIsDraggingContent?.(false)
    resetDragStates()
  }

  const onDragEnter = (e: React.DragEvent) => {
    if (isMomentDragActive) return
    e.preventDefault()
    setDragCount(dragCount + 1)
    setHasPlaceholder(true)
  }

  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    const nextDragCount = dragCount - 1

    setDragCount(nextDragCount)

    if (nextDragCount === 0) {
      setHasPlaceholder(false)
    }
  }

  const onDrop = ({ e, track }: { e: React.DragEvent; track: TrackEvent }) => {
    e.preventDefault()
    updateContentFromDrop({
      contentIndex: index,
      dropEvent: e,
      moment,
      onMoveContent,
      onReorderContent,
      resetDragStates,
      track,
    })
  }

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const renderLoader = () => (
    <div className={kuiperClassNames('contentListItem-loaderWrapper')}>
      <DotLoader height={10} />
    </div>
  )

  const renderStatusIcon = () => (
    <ApprovalStatus content={content} hideDeliveryRing tabIndex={-1} />
  )

  const renderCalendarIcon = () => {
    if (!content.scheduled_at) {
      return null
    }

    const dateText = new Date(content.scheduled_at).toLocaleDateString()

    return (
      <Tooltip content={`Scheduled for ${dateText}`} side="top" zIndex={9999}>
        <span
          className={kuiperClassNames('contentListItem-calendarIconWrapper')}
        >
          <Icon
            ariaHidden={true}
            glyph="gem-calendar"
            width={12}
            height={12}
            color="#a0aeb9"
          />
        </span>
      </Tooltip>
    )
  }

  const renderTrigger = () => (
    <div className={kuiperClassNames('itemInternals')}>
      {serviceIcon && (
        <ChannelIcon
          iconSize={12}
          imageSrc={serviceIcon.assetSrc}
          channelColor={serviceIcon.color}
          channelName={serviceIcon.name}
          channelIconName={serviceIcon.iconName ?? undefined}
          channelSystemName={serviceIcon.systemName}
          privateForUser={false}
        />
      )}
      <div className={kuiperClassNames('contentListItem-title')}>
        {contentName}
      </div>
      {isScheduledAfterMoment && renderCalendarIcon()}
      {content.isSaving ? renderLoader() : renderStatusIcon()}
    </div>
  )

  const onRenameContent = (id: string, contentName: string) => {
    setContentName(contentName)
    contextMenuCallbacks.renameContent(id, contentName)
  }

  const renderRenamePopover = () => (
    <RenamePopover
      closePopover={closeRenamePopover}
      entity={content}
      isOpen={isRenamePopoverOpen}
      label="Rename Content"
      maxLength={100}
      renameEntity={onRenameContent}
      trigger={renderTrigger()}
    />
  )

  const renderInternals = ({ track }: { track: TrackEvent }) => {
    const onKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        onClickContent({ navigateTo, track })
      }
    }

    /* eslint-disable jsx-a11y/no-noninteractive-tabindex, jsx-a11y/no-static-element-interactions */
    return (
      <div
        className={kuiperClassNames(
          'contentListItem',
          {
            hasPlaceholder,
            renamePopoverOpen: isRenamePopoverOpen,
            isSaving: content.isSaving,
          },
          className
        )}
        tabIndex={0}
        onClick={() => onClickContent({ navigateTo, track })}
        onKeyDown={onKeyDown}
        onDragStart={(e) => onDragContentListItem({ e, track })}
        onDrop={(e) => onDrop({ e, track })}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onContextMenu={onContextMenu}
        draggable={isDraggable()}
      >
        {renderWithPopovers()}

        <div className={kuiperClassNames('contentPlaceholder')} />
      </div>
    )
    /* eslint-enable jsx-a11y/no-noninteractive-tabindex, jsx-a11y/no-static-element-interactions */
  }

  const renderWithPopovers = () => {
    if (isRenamePopoverOpen) return renderRenamePopover()

    return renderTrigger()
  }

  const renderPlaceholder = () => (
    <div
      onDragEnd={() => onDragEnd()}
      className={kuiperClassNames('contentListItemDragShadow')}
    />
  )

  return (
    <AnalyticsConsumer>
      {({ track }) =>
        isDragging ? renderPlaceholder() : renderInternals({ track })
      }
    </AnalyticsConsumer>
  )
}

export default DefaultContentListItem
